import { Navigate, Route, Routes } from "react-router-dom";
import { useEffect, useState } from "react";

import { SignInPage } from "./pages/SignInPage";
import { SetPasswordPage } from "./pages/SetPasswordPage";
import { RegisterPage } from "./pages/auth/RegisterPage";
import { ForgotPasswordPage } from "./pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/auth/ResetPasswordPage";
import { MfaChallengePage } from "./pages/MfaChallengePage";
import { MfaEnrollmentPage } from "./pages/auth/MfaEnrollmentPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DeviceListPage } from "./pages/devices/DeviceListPage";
import { DeviceCreatePage } from "./pages/devices/DeviceCreatePage";
import { DeviceDetailPage } from "./pages/devices/DeviceDetailPage";
import { DeviceShadowSettingsPage } from "./pages/devices/DeviceShadowSettingsPage";
import { DeviceLogsPage } from "./pages/devices/DeviceLogsPage";
import { FirmwareListPage } from "./pages/firmware/FirmwareListPage";
import { FirmwareUploadPage } from "./pages/firmware/FirmwareUploadPage";
import { ResourceListPage } from "./pages/resources/ResourceListPage";
import { ResourceDetailPage } from "./pages/resources/ResourceDetailPage";
import { TaskListPage } from "./pages/tasks/TaskListPage";
import { TaskCreatePage } from "./pages/tasks/TaskCreatePage";
import { TaskDetailPage } from "./pages/tasks/TaskDetailPage";
import { GroupListPage } from "./pages/groups/GroupListPage";
import { GroupDetailPage } from "./pages/groups/GroupDetailPage";
import { SettingsGeneralPage } from "./pages/settings/SettingsGeneralPage";
import { GroupKeysPage } from "./pages/settings/GroupKeysPage";
import { ApiCredentialsPage } from "./pages/settings/ApiCredentialsPage";
import { PasskeysPage } from "./pages/settings/PasskeysPage";
import { UsagePage } from "./pages/settings/UsagePage";
import { UserListPage } from "./pages/users/UserListPage";
import { ProfilePage } from "./pages/ProfilePage";
import { AuditLogPage } from "./pages/AuditLogPage";
import { NotificationsPage } from "./pages/NotificationsPage";
import { NotificationPreferencesPage } from "./pages/NotificationPreferencesPage";
import { HelpCenterPage } from "./pages/help/HelpCenterPage";
import { AdminDashboardPage } from "./pages/admin/AdminDashboardPage";
import { AdminDevicesPage } from "./pages/admin/AdminDevicesPage";
import { AdminCompaniesPage } from "./pages/admin/AdminCompaniesPage";
import { AdminCompanyDetailPage } from "./pages/admin/AdminCompanyDetailPage";
import { AdminDeviceTypesPage } from "./pages/admin/AdminDeviceTypesPage";
import { AdminUsersPage } from "./pages/admin/AdminUsersPage";
import { Shell } from "./components/Shell";
import { RequireRole } from "./components/RequireRole";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { getActingCompanyName, getActingTenantId, getIdToken, getRole, isActingAsCompany } from "./auth/session";
import { refreshSession } from "./auth/cognito";

function RequireAuth({ children }: { children: JSX.Element }) {
  return getIdToken() ? children : <Navigate to="/sign-in" replace />;
}

function DeviceListRoute() {
  if (getRole() === "super_admin" && !isActingAsCompany()) {
    return <Navigate to="/admin/devices" replace />;
  }
  if (getRole() === "super_admin" && isActingAsCompany()) {
    return (
      <AdminDevicesPage
        scopedTenantId={getActingTenantId() ?? undefined}
        scopedCompanyName={getActingCompanyName() ?? undefined}
      />
    );
  }

  return <DeviceListPage />;
}

export function App() {
  const [sessionChecked, setSessionChecked] = useState(() => !!getIdToken());

  useEffect(() => {
    let cancelled = false;

    async function bootstrapSession() {
      try {
        if (!getIdToken()) {
          await refreshSession();
        }
      } catch {
        // Failed refresh should fall through to the public sign-in route
        // instead of leaving the app on the restore splash forever.
      } finally {
        if (!cancelled) {
          setSessionChecked(true);
        }
      }
    }

    void bootstrapSession();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!sessionChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-riot-navy-950 text-sm text-riot-navy-300">
        Restoring session...
      </div>
    );
  }

  return (
    <Routes>
      {/* Public auth routes */}
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/mfa-challenge" element={<MfaChallengePage />} />
      <Route path="/mfa-enrollment" element={<MfaEnrollmentPage />} />
      <Route path="/set-password" element={<SetPasswordPage />} />

      {/* Authenticated routes inside Shell layout */}
      <Route
        element={
          <RequireAuth>
            <ErrorBoundary>
              <Shell />
            </ErrorBoundary>
          </RequireAuth>
        }
      >
        {/* Operational */}
        <Route path="/" element={<DashboardPage />} />
        <Route path="/devices" element={<DeviceListRoute />} />
        <Route path="/devices/new" element={<RequireRole minRole="super_admin"><DeviceCreatePage /></RequireRole>} />
        <Route path="/devices/:imei/shadow-settings" element={<RequireRole minRole="tenant_admin"><DeviceShadowSettingsPage /></RequireRole>} />
        <Route path="/devices/:imei" element={<DeviceDetailPage />} />
        <Route path="/device-logs" element={<DeviceLogsPage />} />
        <Route path="/firmware" element={<FirmwareListPage />} />
        <Route path="/firmware/upload" element={<RequireRole minRole="tenant_admin"><FirmwareUploadPage /></RequireRole>} />
        <Route path="/firmware/:id/edit" element={<RequireRole minRole="tenant_admin"><FirmwareUploadPage /></RequireRole>} />
        <Route path="/firmware/:id" element={<FirmwareListPage />} />
        <Route path="/resources" element={<ResourceListPage />} />
        <Route path="/resources/:id" element={<ResourceDetailPage />} />
        <Route path="/tasks" element={<TaskListPage />} />
        <Route path="/tasks/create" element={<RequireRole minRole="operator"><TaskCreatePage /></RequireRole>} />
        <Route path="/tasks/:id" element={<TaskDetailPage />} />
        <Route path="/groups" element={<GroupListPage />} />
        <Route path="/groups/:id" element={<GroupDetailPage />} />

        {/* Management (tenant_admin+) */}
        <Route path="/settings" element={<RequireRole minRole="tenant_admin"><SettingsGeneralPage /></RequireRole>} />
        <Route path="/settings/group-keys" element={<RequireRole minRole="tenant_admin"><GroupKeysPage /></RequireRole>} />
        <Route path="/settings/api-credentials" element={<RequireRole minRole="tenant_admin"><ApiCredentialsPage /></RequireRole>} />
        <Route path="/settings/passkeys" element={<PasskeysPage />} />
        <Route path="/settings/usage" element={<RequireRole minRole="tenant_admin"><UsagePage /></RequireRole>} />
        <Route path="/users" element={<RequireRole minRole="tenant_admin"><UserListPage /></RequireRole>} />
        <Route path="/audit-log" element={<RequireRole minRole="tenant_admin"><AuditLogPage /></RequireRole>} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/help" element={<HelpCenterPage />} />
        <Route path="/help/:articleId" element={<HelpCenterPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/notifications/preferences" element={<NotificationPreferencesPage />} />

        {/* Platform Admin (super_admin only) */}
        <Route path="/admin/dashboard" element={<RequireRole minRole="super_admin"><AdminDashboardPage /></RequireRole>} />
        <Route path="/admin/devices" element={<RequireRole minRole="super_admin"><AdminDevicesPage /></RequireRole>} />
        <Route path="/admin/devices/:imei/shadow-settings" element={<RequireRole minRole="super_admin"><DeviceShadowSettingsPage /></RequireRole>} />
        <Route path="/admin/companies" element={<RequireRole minRole="super_admin"><AdminCompaniesPage /></RequireRole>} />
        <Route path="/admin/companies/:tenantId" element={<RequireRole minRole="super_admin"><AdminCompanyDetailPage /></RequireRole>} />
        <Route path="/admin/device-types" element={<RequireRole minRole="super_admin"><AdminDeviceTypesPage /></RequireRole>} />
        <Route path="/admin/users" element={<RequireRole minRole="super_admin"><AdminUsersPage /></RequireRole>} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
